import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { debugLog, debugError } from '@/utils/debugLogger';

/**
 * Session expiry warning hook.
 * Shows a toast ~5 minutes before the access token expires with an "Estendi sessione" action.
 * Auto-refreshes if the user clicks; otherwise Supabase's built-in refresh may still handle it.
 */
const WARN_MS = 5 * 60 * 1000;
const MIN_INTERVAL = 30 * 1000;

export function useSessionExpiryWarning() {
  const warnedForExpiresAt = useRef<number | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let cancelled = false;

    const scheduleFromSession = async () => {
      if (cancelled) return;
      try {
        const { data } = await supabase.auth.getSession();
        const expiresAt = data.session?.expires_at; // seconds
        if (!expiresAt) {
          clearTimer();
          return;
        }

        const expiresMs = expiresAt * 1000;
        const now = Date.now();
        const warnAt = expiresMs - WARN_MS;
        const delay = Math.max(MIN_INTERVAL, warnAt - now);

        clearTimer();
        timerRef.current = window.setTimeout(() => {
          if (cancelled) return;
          if (warnedForExpiresAt.current === expiresAt) return;
          warnedForExpiresAt.current = expiresAt;

          toast('La sessione sta per scadere', {
            description: 'Estendi la sessione per continuare a lavorare senza interruzioni.',
            duration: 60_000,
            action: {
              label: 'Estendi',
              onClick: async () => {
                try {
                  const { error } = await supabase.auth.refreshSession();
                  if (error) throw error;
                  toast.success('Sessione estesa');
                } catch (e) {
                  debugError('auth', 'Session refresh failed', e, 'useSessionExpiryWarning');
                  toast.error('Impossibile estendere la sessione. Effettua di nuovo l\'accesso.');
                }
              },
            },
          });
        }, delay);

        debugLog('auth', `Session warning scheduled in ${Math.round(delay / 1000)}s`, undefined, 'useSessionExpiryWarning');
      } catch (e) {
        debugError('auth', 'Failed to schedule session warning', e, 'useSessionExpiryWarning');
      }
    };

    const clearTimer = () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    scheduleFromSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
        warnedForExpiresAt.current = null;
        scheduleFromSession();
      } else if (event === 'SIGNED_OUT') {
        clearTimer();
        warnedForExpiresAt.current = null;
      }
    });

    return () => {
      cancelled = true;
      clearTimer();
      subscription.unsubscribe();
    };
  }, []);
}
